"use client";

import type React from "react";
import { createContext, useCallback, useContext } from "react";
import { useVipStatus as useVipStatusHook } from "@/hooks/useVipStatus";
import { VIP_FEATURES, type VipFeature } from "@/lib/vip/feature-access";
import { vipCacheManager } from "@/utils/cache-manager";
import { type VipInfo, VipLevel } from "@/types/vip";
import { useIdentity } from "./IdentityContext";

interface VipContextValue {
	// 状态
	vipInfo: VipInfo;
	isVip: boolean;
	isLoading: boolean;
	error: string | null;

	// 方法
	refresh: () => Promise<void>;
	checkAccess: (feature: VipFeature) => boolean;

	// 缓存控制
	clearCache: () => void;
	getCacheStatus: () => {
		babyOdinCached: boolean;
		whitelistCached: boolean;
		lastUpdate: number | null;
	};
}

const VipContext = createContext<VipContextValue | undefined>(undefined);

const CACHE_KEY_BABY_ODIN = "baby_odin_lp_balance";
const CACHE_KEY_WHITELIST = "whitelist_info";

export function VipProvider({ children }: { children: React.ReactNode }) {
	const { principal } = useIdentity();
	const { vipInfo, isVip, isLoading, error, refresh } = useVipStatusHook();

	const checkAccess = useCallback(
		(feature: VipFeature): boolean => {
			const allowedLevels = VIP_FEATURES[feature] as unknown as VipLevel[];
			if (!allowedLevels) return true; // 未定义的功能默认允许

			return allowedLevels.includes(vipInfo.level);
		},
		[vipInfo.level],
	);

	const clearCache = useCallback(() => {
		if (principal) {
			vipCacheManager.delete(`${CACHE_KEY_BABY_ODIN}_${principal}`);
			vipCacheManager.delete(`${CACHE_KEY_WHITELIST}_${principal}`);
		}
	}, [principal]);

	const getCacheStatus = useCallback(() => {
		const babyOdinCached = principal
			? vipCacheManager.has(`${CACHE_KEY_BABY_ODIN}_${principal}`)
			: false;
		const whitelistCached = principal
			? vipCacheManager.has(`${CACHE_KEY_WHITELIST}_${principal}`)
			: false;

		return {
			babyOdinCached,
			whitelistCached,
			lastUpdate: null,
		};
	}, [principal]);

	const contextValue: VipContextValue = {
		vipInfo,
		isVip,
		isLoading,
		error,
		refresh,
		checkAccess,
		clearCache,
		getCacheStatus,
	};

	return (
		<VipContext.Provider value={contextValue}>{children}</VipContext.Provider>
	);
}

// Hook to use VIP context
export function useVip() {
	const context = useContext(VipContext);
	if (context === undefined) {
		throw new Error("useVip must be used within VipProvider");
	}
	return context;
}

// Backward compatibility hook
export function useVipStatus() {
	const { vipInfo, isVip, isLoading, error, refresh } = useVip();

	return {
		vipInfo,
		isVip,
		isLoading,
		error,
		refresh,
	};
}
